import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Footer from "@/components/skinfill/Footer";

const Section = ({ n, title, children }) => (
  <section className="border-t border-[#EADECE] pt-8 mt-8 first:border-t-0 first:pt-0 first:mt-0">
    <h2 className="font-serif font-normal text-[#1C1417] text-xl sm:text-2xl tracking-tight">
      <span className="font-mono text-xs text-[#A31D4F] tracking-[0.25em] mr-3 align-middle">{n}.</span>
      {title}
    </h2>
    <div className="mt-4 space-y-3 text-[15px] font-light text-[#4a4144] leading-relaxed max-w-3xl">{children}</div>
  </section>
);

const Privacy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="privacy-page" className="bg-[#FAF7F5] min-h-screen">
      <header className="sticky top-0 z-50 bg-[#FAF7F5]/85 backdrop-blur-xl border-b border-[#EADECE]/60">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" data-testid="privacy-logo-link" className="font-serif text-lg tracking-[0.08em] text-[#1C1417]">
            SKINFILL <span className="italic text-[#D84A75]">BACIO</span>
            <sup className="text-[9px] ml-0.5">®</sup>
          </Link>
          <Link
            to="/"
            data-testid="privacy-back-link"
            className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-[#A31D4F] hover:text-[#86143E] transition-colors"
          >
            <ArrowLeft size={14} />
            Back to site
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
        <p className="font-mono text-[11px] sm:text-xs uppercase tracking-[0.28em] text-[#A31D4F]">Legal</p>
        <h1 className="font-serif font-light text-[#1C1417] text-4xl sm:text-6xl tracking-tight mt-5 leading-[1.02]">
          Privacy <span className="italic text-[#A31D4F]">Policy</span>
        </h1>
        <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.22em] text-[#6E6266]">
          Effective date: 27 September 2026
        </p>

        <div className="mt-12 space-y-2 text-[15px] font-light text-[#4a4144] leading-relaxed max-w-3xl">
          <p>
            This Privacy Policy explains how Dermalis Limited (New Zealand) and Dermalis Pty Ltd (Australia)
            (&ldquo;Dermalis&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo; or &ldquo;our&rdquo;) collect, use, store and
            disclose personal information in connection with the Skinfill Bacio® Australia and New Zealand website,
            our communications and related business activities.
          </p>
          <p>Dermalis is the distributor of Skinfill Bacio® in Australia and New Zealand.</p>
          <p>
            We are committed to respecting your privacy and handling personal information responsibly and in
            accordance with applicable privacy laws, including the Privacy Act 1988 (Cth) and Australian Privacy
            Principles where applicable in Australia, and the Privacy Act 2020 in New Zealand.
          </p>
        </div>

        <div className="mt-14">
          <Section n="1" title="Information we may collect">
            <p>Depending on how you interact with us, we may collect information including:</p>
            <ul className="list-disc pl-5 space-y-1.5 marker:text-[#D84A75]">
              <li>your name;</li>
              <li>email address;</li>
              <li>telephone or mobile number;</li>
              <li>clinic, company or business name;</li>
              <li>professional title or occupation;</li>
              <li>city, state, region and country;</li>
              <li>information submitted through enquiry or contact forms;</li>
              <li>information relating to professional or wholesale enquiries;</li>
              <li>correspondence between you and Dermalis;</li>
              <li>information relating to training, events or product enquiries;</li>
              <li>
                website usage and technical information such as browser type, device information, IP address and pages
                visited;
              </li>
              <li>marketing preferences and communication history.</li>
            </ul>
            <p>We generally do not require consumers to provide health or medical information through this website.</p>
            <p>
              Please do not submit confidential medical records, detailed medical histories, photographs of medical
              conditions or other sensitive health information through general website enquiry forms.
            </p>
          </Section>

          <Section n="2" title="How we collect information">
            <p>We may collect personal information when you:</p>
            <ul className="list-disc pl-5 space-y-1.5 marker:text-[#D84A75]">
              <li>submit an enquiry through the website;</li>
              <li>contact Dermalis by email, telephone or another communication channel;</li>
              <li>request information about Skinfill Bacio®;</li>
              <li>enquire about becoming a Skinfill Bacio® clinic;</li>
              <li>request professional education or training;</li>
              <li>attend a Dermalis event or training session;</li>
              <li>subscribe to marketing communications;</li>
              <li>interact with our website, advertisements or social media;</li>
              <li>communicate with an authorised Dermalis representative.</li>
            </ul>
            <p>
              Where permitted by law, we may also receive professional or business information from publicly available
              sources, professional directories, business contacts, service providers or other legitimate sources.
            </p>
          </Section>

          <Section n="3" title="Why we collect and use personal information">
            <p>We may use personal information to:</p>
            <ul className="list-disc pl-5 space-y-1.5 marker:text-[#D84A75]">
              <li>respond to enquiries;</li>
              <li>provide requested information;</li>
              <li>communicate with clinics and healthcare or aesthetic professionals;</li>
              <li>process account or wholesale enquiries;</li>
              <li>arrange product education or training;</li>
              <li>administer events;</li>
              <li>provide customer and professional support;</li>
              <li>manage our business relationship with clinics and customers;</li>
              <li>improve our website, services and communications;</li>
              <li>maintain website security;</li>
              <li>meet regulatory, legal and compliance obligations;</li>
              <li>maintain appropriate business and transaction records;</li>
              <li>
                send marketing or product communications where permitted by law and in accordance with your
                preferences.
              </li>
            </ul>
            <p>
              We will only use personal information for purposes permitted by applicable law or for other purposes for
              which you have provided consent.
            </p>
          </Section>

          <Section n="4" title="Marketing communications">
            <p>
              Where you have consented, or where otherwise permitted by applicable law, Dermalis may send information
              relating to products, professional education, events, training, offers or other relevant Dermalis
              activities.
            </p>
            <p>
              You may unsubscribe from marketing communications at any time using the unsubscribe function provided in
              the communication or by contacting us.
            </p>
            <p>
              Unsubscribing from marketing communications does not prevent us from sending non-marketing communications
              that may be necessary in connection with an existing business relationship, enquiry, transaction, safety
              communication or legal obligation.
            </p>
          </Section>

          <Section n="5" title="Disclosure of personal information">
            <p>We may disclose personal information where reasonably necessary to:</p>
            <ul className="list-disc pl-5 space-y-1.5 marker:text-[#D84A75]">
              <li>Dermalis employees and authorised representatives;</li>
              <li>related Dermalis entities;</li>
              <li>website hosting and technology providers;</li>
              <li>CRM, email and communications providers;</li>
              <li>professional advisers;</li>
              <li>training or event service providers;</li>
              <li>logistics and customer-support providers;</li>
              <li>regulatory or government authorities where required by law;</li>
              <li>other service providers acting on our behalf.</li>
            </ul>
            <p>
              Service providers are expected to handle information only for the purposes for which it has been provided
              and subject to appropriate confidentiality, privacy and security obligations.
            </p>
            <p>We do not sell personal information to third parties.</p>
          </Section>

          <Section n="6" title="Overseas storage and disclosure">
            <p>
              Some of the technology, cloud, communications or service providers used by Dermalis may store or process
              information outside Australia or New Zealand.
            </p>
            <p>
              Where personal information is transferred internationally, we take reasonable steps appropriate to the
              circumstances to ensure that it is handled securely and consistently with applicable privacy
              requirements.
            </p>
            <p>
              The countries in which information may be processed can vary depending on the technology providers we
              use from time to time.
            </p>
          </Section>

          <Section n="7" title="Cookies and website technologies">
            <p>Our website may use cookies and similar technologies to:</p>
            <ul className="list-disc pl-5 space-y-1.5 marker:text-[#D84A75]">
              <li>operate the website;</li>
              <li>remember preferences;</li>
              <li>understand how visitors use the website;</li>
              <li>measure website performance;</li>
              <li>improve content and user experience;</li>
              <li>support security and fraud prevention;</li>
              <li>measure advertising or campaign effectiveness, where applicable.</li>
            </ul>
            <p>Some cookies may be provided by third-party services.</p>
            <p>Where required, visitors will be given appropriate options to manage non-essential cookies.</p>
            <p>
              You can also change your browser settings to restrict or delete cookies. Disabling certain cookies may
              affect some website functions.
            </p>
          </Section>

          <Section n="8" title="Analytics and third-party services">
            <p>
              We may use third-party analytics, hosting, video, communications, social-media or marketing services.
            </p>
            <p>
              These services may collect technical information about your interaction with the website in accordance
              with their own privacy policies.
            </p>
            <p>
              Where the website contains links to external websites or platforms, Dermalis is not responsible for the
              privacy practices of those external services.
            </p>
          </Section>

          <Section n="9" title="Security">
            <p>
              Dermalis takes reasonable administrative, technical and organisational measures designed to protect
              personal information against:
            </p>
            <ul className="list-disc pl-5 space-y-1.5 marker:text-[#D84A75]">
              <li>loss;</li>
              <li>unauthorised access;</li>
              <li>misuse;</li>
              <li>alteration;</li>
              <li>disclosure; or</li>
              <li>destruction.</li>
            </ul>
            <p>No online transmission or electronic storage system can be guaranteed to be completely secure.</p>
          </Section>

          <Section n="10" title="Retention">
            <p>
              We retain personal information only for as long as reasonably necessary for the purposes for which it
              was collected, including legitimate business, regulatory, accounting and legal requirements.
            </p>
            <p>
              When information is no longer reasonably required, we may securely delete, destroy or de-identify it,
              subject to applicable legal retention requirements.
            </p>
          </Section>

          <Section n="11" title="Access and correction">
            <p>
              You may request access to personal information that Dermalis holds about you and may request correction
              of information that is inaccurate, incomplete, out of date or misleading.
            </p>
            <p>
              To make a request, contact: Dermalis — Email:{" "}
              <a href="mailto:info@dermalis.com.au" className="text-[#A31D4F] hover:text-[#86143E] underline underline-offset-4 decoration-[#E893AE]/60">
                info@dermalis.com.au
              </a>
            </p>
            <p>We may need to verify your identity before processing an access or correction request.</p>
            <p>Certain legal exceptions may apply to access requests.</p>
          </Section>

          <Section n="12" title="Privacy questions or complaints">
            <p>
              If you have a privacy question, concern or complaint, please contact us at:{" "}
              <a href="mailto:info@dermalis.com.au" className="text-[#A31D4F] hover:text-[#86143E] underline underline-offset-4 decoration-[#E893AE]/60">
                info@dermalis.com.au
              </a>
            </p>
            <p>Please provide sufficient information for us to understand and investigate your concern.</p>
            <p>We will review privacy complaints and respond within a reasonable period.</p>
            <p>
              If you are not satisfied with our response, you may have the right to contact the relevant privacy
              regulator, including:
            </p>
            <ul className="list-disc pl-5 space-y-1.5 marker:text-[#D84A75]">
              <li>the Office of the Australian Information Commissioner (OAIC) in Australia; or</li>
              <li>the Office of the Privacy Commissioner in New Zealand.</li>
            </ul>
          </Section>

          <Section n="13" title="Children">
            <p>This website is intended primarily for adults and healthcare or aesthetic professionals.</p>
            <p>We do not knowingly seek to collect personal information from children through this website.</p>
          </Section>

          <Section n="14" title="Changes to this Privacy Policy">
            <p>
              We may update this Privacy Policy from time to time to reflect changes in our business, technology,
              regulatory obligations or information-handling practices.
            </p>
            <p>The latest version will be published on this website with an updated effective date.</p>
          </Section>

          <Section n="15" title="Contact">
            <p>For privacy enquiries:</p>
            <p>
              Dermalis — Australia &amp; New Zealand
              <br />
              Email:{" "}
              <a href="mailto:info@dermalis.com.au" className="text-[#A31D4F] hover:text-[#86143E] underline underline-offset-4 decoration-[#E893AE]/60">
                info@dermalis.com.au
              </a>
            </p>
            <p>Skinfill Bacio® is distributed in Australia and New Zealand by Dermalis.</p>
          </Section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Privacy;
